import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { usePendientes } from "../hooks/usePendientes";
import { useClientes } from "../hooks/useClientes";
import { t } from "../i18n";
import { formatAmount } from "../config/currencies";
import {
  CircleCheck, Search, Plus, Contact, Calendar, TrendingUp, Wallet,
  TrendingDown, Pencil, AlertTriangle, CalendarClock, Banknote, Trash2,
  Hourglass, Paperclip, ArrowUpDown,
} from "lucide-react";
import EditPendienteModal from "./EditPendienteModal";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function fmtCreatedAt(createdAt, lang) {
  if (!createdAt) return "";
  // Firestore Timestamp tiene .toDate(); si ya es Date, se usa directo
  const d = typeof createdAt.toDate === "function" ? createdAt.toDate() : new Date(createdAt);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang==="en" ? "en-GB" : "es-ES", { day:"2-digit", month:"2-digit", year:"numeric" });
}

// Devuelve el objeto Date de creación (o null), para poder ofrecer
// "fecha de creación" como opción rápida al registrar un pago.
function getCreatedDateObj(createdAt) {
  if (!createdAt) return null;
  const d = typeof createdAt.toDate === "function" ? createdAt.toDate() : new Date(createdAt);
  return isNaN(d.getTime()) ? null : d;
}

// Formatea una fecha como YYYY-MM-DD, el formato que espera <input type="date">
function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function Pendientes() {
  const { profile, lang, currency } = useAuth();
  const { addEntry } = useEntries();
  const { pendientes, loading, addPendiente, deletePendiente, registrarPago, updatePendiente, getPagado, getRestante } = usePendientes();
  const { clientes } = useClientes();
  const c = profile?.config || {};
  const T = (key) => t(lang, key);
  const fmt = n => formatAmount(n, currency);

  const [type,    setType]    = useState("expense");
  const [desc,    setDesc]    = useState("");
  const [cat,     setCat]     = useState("");
  const [payment, setPayment] = useState("");
  const [amount,  setAmount]  = useState("");
  const [due,     setDue]     = useState("");
  const [realizado, setRealizado] = useState("");
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");
  const [filter,  setFilter]  = useState("todos");
  const [expanded, setExpanded] = useState(null);

  // Editar abre el mismo formulario de Añadir Entrada (adaptado a
  // pendiente) en una ventana superpuesta — EditPendienteModal — en vez de
  // un formulario aparte dentro de la fila.
  const [editingPendiente, setEditingPendiente] = useState(null);

  // Abono parcial
  const [payingId, setPayingId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payNota, setPayNota] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payError, setPayError] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  const cats = type === "income" ? (c.income_cats||[]) : (c.expense_cats||[]);
  const accounts = (c.accounts||[]).map(a => a.name);
  const income_label = c.income_label || (lang==="en" ? "Income" : "Ingresos");
  const expense_label = c.expense_label || (lang==="en" ? "Expenses" : "Gastos");

  // Campos personalizados: solo en modo negocio, y solo los activados/nombrados
  const isNegocio = profile?.perfil === "negocio";
  const activeCustomFields = isNegocio
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];
  const [customValues, setCustomValues] = useState({ custom1: "", custom2: "", custom3: "" });

  // Contacto vinculado (solo modo negocio) — mismo buscador que en Añadir
  // e Historial: se escribe y filtra, en vez de un <select> largo.
  const [clienteId, setClienteId] = useState("");
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [editClienteSearch, setEditClienteSearch] = useState("");
  const [editClienteSearchOpen, setEditClienteSearchOpen] = useState(false);
  const clientesById = clientes.reduce((acc, cl) => { acc[cl.id] = cl; return acc; }, {});

  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const today = new Date(); today.setHours(0,0,0,0);

  const filtered = pendientes.filter(p => {
    if (filter === "cobros" && p.type !== "income") return false;
    if (filter === "pagos" && p.type !== "expense") return false;
    if (filter === "vencidos") {
      if (!p.due) return false;
      const d = new Date(p.due); d.setHours(0,0,0,0);
      if (d >= today) return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = [
        p.desc, p.cat, p.payment,
        p.custom1, p.custom2, p.custom3,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const totalCobros = filtered.filter(p=>p.type==="income").reduce((s,p)=>s+getRestante(p),0);
  const totalPagos  = filtered.filter(p=>p.type==="expense").reduce((s,p)=>s+getRestante(p),0);

  async function handleAdd(e) {
    e.preventDefault();
    setError("");
    const amt = parseFloat(amount.replace(",","."));
    if (!amt || amt <= 0) { setError(lang==="en"?"Enter a valid amount":"Introduce un importe válido"); return; }
    if (!desc.trim()) { setError(lang==="en"?"Write a description":"Escribe una descripción"); return; }

    // "Ya cobrado/pagado (opcional)": igual que en Añadir — si se indica una
    // parte ya realizada, esa parte se registra como entrada real (Historial/
    // Saldo Disponible) y solo el resto se guarda como pendiente.
    const realizadoAmt = Math.max(0, parseFloat((realizado||"0").replace(",",".")) || 0);
    if (realizadoAmt > amt) {
      setError(lang==="en"
        ? "The amount already received/paid can't be higher than the total"
        : "Lo ya cobrado/pagado no puede ser mayor que el importe total");
      return;
    }
    const restanteAmt = Math.max(0, amt - realizadoAmt);

    const customData = {};
    activeCustomFields.forEach(f => {
      if (customValues[f.key]) customData[f.key] = customValues[f.key];
    });

    if (realizadoAmt > 0) {
      const now = new Date();
      const datetime = `${String(now.getDate()).padStart(2,"0")}/${String(now.getMonth()+1).padStart(2,"0")}/${now.getFullYear()} ${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
      const res = await addEntry({
        type, cat: cat||"General", desc: desc.trim(), payment, amount: realizadoAmt,
        month: MONTHS[now.getMonth()], year: now.getFullYear(), datetime,
        ...(clienteId ? { clienteId } : {}),
        ...customData,
      });
      if (res.error) { setError(res.error); return; }
    }
    if (restanteAmt > 0) {
      await addPendiente({
        type, desc: desc.trim(), cat: cat||"General", payment, amount: restanteAmt, due: due||null,
        ...(clienteId ? { clienteId } : {}),
        ...customData,
      });
    }

    if (realizadoAmt > 0 && restanteAmt > 0) {
      setSuccess(lang==="en"
        ? `${fmt(realizadoAmt)} added to History, ${fmt(restanteAmt)} saved as pending`
        : `${fmt(realizadoAmt)} añadidos al Historial, ${fmt(restanteAmt)} guardados como pendiente`);
    } else if (restanteAmt > 0) {
      setSuccess(lang==="en"?"Pending entry added":"Pendiente añadido");
    } else {
      setSuccess(lang==="en" ? "Entry saved, nothing left pending" : "Entrada guardada, no queda nada pendiente");
    }
    setAmount(""); setDesc(""); setCat(""); setPayment(""); setDue(""); setRealizado("");
    setCustomValues({ custom1: "", custom2: "", custom3: "" });
    setClienteId(""); setClienteSearch("");
    setTimeout(()=>setSuccess(""), 3000);
  }

  function startPay(p, ev) {
    if (ev) ev.stopPropagation();
    setPayingId(p.id);
    setPayAmount(String(getRestante(p).toFixed(2)));
    setPayNota("");
    setPayError("");
  }

  async function confirmPay(p) {
    setPayError("");
    const restante = getRestante(p);
    const monto = parseFloat(String(payAmount).replace(",", "."));
    if (!monto || monto <= 0) {
      setPayError(lang==="en" ? "Enter a valid amount" : "Introduce un importe válido");
      return;
    }
    if (monto > restante + 0.0001) {
      setPayError(lang==="en"
        ? `Amount can't exceed the remaining ${fmt(restante)}`
        : `El importe no puede superar el restante de ${fmt(restante)}`);
      return;
    }
    setPaySaving(true);
    await registrarPago(p, monto, addEntry, payNota, payDate);
    setPaySaving(false);
    setPayingId(null);
    setPayAmount("");
    setPayNota("");
    setPayDate("");
  }

  function startEdit(p, ev) {
    ev.stopPropagation();
    setEditingPendiente(p);
  }

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><Hourglass size={22}/> {lang==="en"?"Pending":"Pendientes"}</h2>
          <p className="page-sub">{lang==="en"?"Manage pending payments and collections":"Gestiona cobros y pagos pendientes"}</p>
        </div>
      </div>

      {/* Resumen */}
      {pendientes.length > 0 && (
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
          <div style={{background:"var(--green-l)",border:"1px solid var(--green)",borderRadius:"var(--radius-sm)",
                       padding:"10px 16px",fontSize:13,fontWeight:600,color:"var(--green)",display:"flex",alignItems:"center",gap:6}}>
            <TrendingUp size={14}/> {lang==="en"?"To collect":"Por cobrar"}: {fmt(pendientes.filter(p=>p.type==="income").reduce((s,p)=>s+getRestante(p),0))}
          </div>
          <div style={{background:"var(--red-l)",border:"1px solid var(--red)",borderRadius:"var(--radius-sm)",
                       padding:"10px 16px",fontSize:13,fontWeight:600,color:"var(--red)",display:"flex",alignItems:"center",gap:6}}>
            <TrendingDown size={14}/> {lang==="en"?"To pay":"Por pagar"}: {fmt(pendientes.filter(p=>p.type==="expense").reduce((s,p)=>s+getRestante(p),0))}
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,440px),1fr))",gap:20,alignItems:"start"}}>

        {/* Formulario */}
        <div className="card">
          {!showForm ? (
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"12px",display:"flex",alignItems:"center",gap:6}}
                    onClick={()=>setShowForm(true)}>
              <Plus size={16}/> {lang==="en"?"Add entry":"Añadir entrada"}
            </button>
          ) : (
          <>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}><Plus size={16}/> {lang==="en"?"New pending":"Nuevo pendiente"}</h3>
          {error   && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success" style={{display:"flex",alignItems:"center",gap:6}}><CircleCheck size={15}/> {success}</div>}
          <form onSubmit={handleAdd}>
            <div className="form-group">
              <label className="form-label">{T("add_type")}</label>
              <div style={{display:"flex",gap:10}}>
                {[["income",income_label,Wallet],["expense",expense_label,TrendingDown]].map(([v,l,Icon])=>(
                  <label key={v} style={{flex:1,cursor:"pointer"}}>
                    <input type="radio" value={v} checked={type===v} onChange={()=>{setType(v);setCat("");}} style={{display:"none"}}/>
                    <div style={{padding:"10px 14px",borderRadius:"var(--radius-sm)",textAlign:"center",
                                 border:`2px solid ${type===v?"var(--blue)":"var(--border)"}`,
                                 background:type===v?"var(--blue-l)":"white",fontSize:14,fontWeight:500,
                                 color:type===v?"var(--blue)":"var(--gray-700)",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <Icon size={15}/> {l}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{T("add_desc")}</label>
              <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)}
                     placeholder={lang==="en"?"E.g: Invoice #001, Rent...":"Ej: Factura #001, Alquiler..."} required/>
            </div>
            <div className="form-group">
              <label className="form-label">{T("add_cat")}</label>
              <select className="form-select" value={cat} onChange={e=>setCat(e.target.value)}>
                <option value="">{T("add_cat_placeholder")}</option>
                {cats.map(c=><option key={c}>{c}</option>)}
                <option value="Otro">{T("add_other")}</option>
              </select>
            </div>
            {accounts.length > 0 && (
              <div className="form-group">
                <label className="form-label">{T("add_account")}</label>
                <select className="form-select" value={payment} onChange={e=>setPayment(e.target.value)}>
                  <option value="">{T("add_no_account")}</option>
                  {accounts.map(a=><option key={a}>{a}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">{T("add_amount")}</label>
              <input className="form-input" type="number" step="0.01" min="0.01"
                     value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" required/>
            </div>
            {isNegocio && (
              <div className="form-group">
                <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><Contact size={14}/> {lang==="en" ? "Contact (optional)" : "Contacto (opcional)"}</label>
                {clienteId ? (
                  <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
                               padding:"10px 12px", background:"var(--gray-50)", borderRadius:8}}>
                    <span style={{fontWeight:600, fontSize:14}}>{clientesById[clienteId]?.nombre || "…"}</span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={()=>setClienteId("")}>
                      {lang==="en" ? "Change" : "Cambiar"}
                    </button>
                  </div>
                ) : (
                  <div style={{position:"relative"}}>
                    <input className="form-input" value={clienteSearch}
                           onChange={e=>{ setClienteSearch(e.target.value); setClienteSearchOpen(true); }}
                           onFocus={()=>setClienteSearchOpen(true)}
                           placeholder={lang==="en" ? "Search contact by name..." : "Buscar contacto por nombre..."}/>
                    {clienteSearchOpen && (
                      <>
                        <div style={{position:"fixed", inset:0, zIndex:19}} onClick={()=>setClienteSearchOpen(false)} />
                        <div style={{position:"absolute", top:"100%", left:0, right:0, marginTop:4, zIndex:20,
                                     background:"var(--card,#fff)", border:"1px solid var(--border)", borderRadius:8,
                                     maxHeight:200, overflowY:"auto", boxShadow:"var(--shadow-md,0 6px 20px rgba(0,0,0,.15))"}}>
                          {(() => {
                            const q = clienteSearch.trim().toLowerCase();
                            const matches = q ? clientes.filter(cl => (cl.nombre||"").toLowerCase().includes(q) || (cl.telefono||"").toLowerCase().includes(q) || (cl.dni||"").toLowerCase().includes(q) || (cl.email||"").toLowerCase().includes(q)) : clientes;
                            if (matches.length === 0) {
                              return <div style={{padding:"10px 12px", fontSize:13, color:"var(--gray-400)"}}>
                                {lang==="en" ? "No matches" : "Sin resultados"}
                              </div>;
                            }
                            return matches.slice(0,8).map(cl => (
                              <div key={cl.id}
                                   onClick={()=>{ setClienteId(cl.id); setClienteSearch(""); setClienteSearchOpen(false); }}
                                   style={{padding:"9px 12px", cursor:"pointer", fontSize:14}}
                                   onMouseEnter={e=>e.currentTarget.style.background="var(--gray-50)"}
                                   onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                {cl.nombre}
                              </div>
                            ));
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeCustomFields.length > 0 && (
              <div className="form-group">
                <label className="form-label">{lang==="en" ? "Extra details" : "Datos adicionales"}</label>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {activeCustomFields.map(f => (
                    <input key={f.key} className="form-input"
                           placeholder={f.name}
                           value={customValues[f.key]}
                           onChange={e=>setCustomValues(v=>({...v,[f.key]:e.target.value}))}/>
                  ))}
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">
                {type==="income"
                  ? (lang==="en" ? "Already received (optional)" : "Ya cobrado (opcional)")
                  : (lang==="en" ? "Already paid (optional)" : "Ya pagado (opcional)")}
              </label>
              <input className="form-input" type="number" step="0.01" min="0"
                     value={realizado} onChange={e=>setRealizado(e.target.value)} placeholder="0.00"/>
              {(() => {
                const realizadoNum = Math.max(0, parseFloat((realizado||"0").replace(",", ".")) || 0);
                const amtNum = parseFloat((amount||"0").replace(",", ".")) || 0;
                if (realizadoNum <= 0) return null;
                const restanteNum = Math.max(0, amtNum - realizadoNum);
                return (
                  <p className="form-hint">
                    {lang==="en"
                      ? `${fmt(realizadoNum)} will be added to History now, ${fmt(restanteNum)} stays pending.`
                      : `${fmt(realizadoNum)} se añadirán ya al Historial, ${fmt(restanteNum)} quedan pendientes.`}
                  </p>
                );
              })()}
            </div>
            <div className="form-group">
              <label className="form-label">{lang==="en"?"Due date (optional)":"Fecha de vencimiento (opcional)"}</label>
              <input className="form-input" type="date" value={due} onChange={e=>setDue(e.target.value)}/>
            </div>
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"12px",display:"flex",alignItems:"center",gap:6}} type="submit">
              <Plus size={16}/> {lang==="en"?"Add pending":"Añadir pendiente"}
            </button>
          </form>
          </>
          )}
        </div>

        {/* Lista */}
        <div>
          {/* Buscador */}
          <div className="form-group" style={{marginBottom:10}}>
            <input className="form-input" value={search} onChange={e=>setSearch(e.target.value)}
                   placeholder={lang==="en" ? "Search by description, category, account..." : "Buscar por descripción, categoría, cuenta..."} />
          </div>

          {/* Filtros */}
          <div className="filters" style={{marginBottom:12}}>
            {[["todos",lang==="en"?"All":"Todos"],["cobros",income_label],["pagos",expense_label],["vencidos",lang==="en"?"Overdue":"Vencidos"]].map(([v,l])=>(
              <button key={v} onClick={()=>setFilter(v)}
                      style={{padding:"6px 14px",borderRadius:20,border:`2px solid ${filter===v?"var(--blue)":"var(--border)"}`,
                              background:filter===v?"var(--blue-l)":"white",color:filter===v?"var(--blue)":"var(--gray-600)",
                              fontSize:13,fontWeight:500,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>

          {/* Total del resultado actual (respeta filtro + búsqueda) */}
          {(search.trim() || filter !== "todos") && filtered.length > 0 && (
            <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
              {totalCobros > 0 && (
                <div style={{background:"var(--green-l,rgba(27,127,58,.1))",borderRadius:20,
                             padding:"8px 14px",fontSize:13,fontWeight:600,color:"var(--green)"}}>
                  {income_label}: {fmt(totalCobros)}
                </div>
              )}
              {totalPagos > 0 && (
                <div style={{background:"var(--red-l,rgba(192,57,43,.1))",borderRadius:20,
                             padding:"8px 14px",fontSize:13,fontWeight:600,color:"var(--red)"}}>
                  {expense_label}: {fmt(totalPagos)}
                </div>
              )}
              {totalCobros > 0 && totalPagos > 0 && (
                <div style={{background:"var(--gray-100)",borderRadius:20,
                             padding:"8px 14px",fontSize:13,fontWeight:600,
                             color:(totalCobros-totalPagos)>=0?"var(--green)":"var(--red)"}}>
                  = {fmt(totalCobros-totalPagos)}
                </div>
              )}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon"><Hourglass size={26}/></div>
              <h3>{lang==="en"?"No pending entries":"Sin pendientes"}</h3>
              <p>{lang==="en"?"Add pending payments or collections":"Añade cobros o pagos pendientes"}</p>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {filtered.map(p => {
                const isOpen = expanded === p.id;
                const isPaying = payingId === p.id;
                const isVencido = p.due && new Date(p.due) < today;
                const pagadoAmount = getPagado(p);
                const restante = getRestante(p);
                const tienePagosParciales = pagadoAmount > 0;
                const createdLabel = fmtCreatedAt(p.createdAt, lang);
                return (
                  <div key={p.id}
                       style={{background:"var(--card)",borderRadius:"var(--radius)",
                               border:`1px solid ${isVencido?"var(--red)":"var(--border)"}`,
                               boxShadow:"0 1px 4px rgba(0,0,0,.05)",overflow:"hidden",cursor:isPaying?"default":"pointer"}}
                       onClick={()=>{ if(!isPaying) setExpanded(isOpen?null:p.id); }}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                        <span style={{flexShrink:0,display:"inline-flex",alignItems:"center",
                                      color:p.isCombined?"var(--blue)":(p.type==="income"?"var(--green)":"var(--red)")}}>
                          {p.isCombined ? <ArrowUpDown size={15}/> : (p.type==="income"?<TrendingUp size={15}/>:<TrendingDown size={15}/>)}
                        </span>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--gray-800)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {p.desc}
                            {p.isCombined && <span style={{marginLeft:6,fontSize:11,fontWeight:500,color:"var(--gray-400)"}}>
                              ({lang==="en"?"combined":"combinada"})
                            </span>}
                          </div>
                          <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2,display:"flex",gap:8,flexWrap:"wrap"}}>
                            {createdLabel && (
                              <span style={{display:"inline-flex",alignItems:"center",gap:4}}><Calendar size={12}/> {lang==="en"?"Created":"Creado"}: {createdLabel}</span>
                            )}
                            {p.due && (
                              <span style={{color:isVencido?"var(--red)":"var(--gray-400)",fontWeight:isVencido?600:400}}>
                                {isVencido && <AlertTriangle size={12} style={{display:"inline",verticalAlign:"-2px",marginRight:3}}/>}{lang==="en"?"Due":"Vence"}: {new Date(p.due).toLocaleDateString("es-ES")}
                              </span>
                            )}
                            {p.receiptPath && (
                              <span style={{display:"inline-flex",alignItems:"center",gap:3,cursor:"pointer",color:"var(--blue)"}}
                                    onClick={async (ev)=>{
                                      ev.stopPropagation();
                                      try {
                                        const { getAttachmentUrl } = await import("./receiptOcr");
                                        const url = await getAttachmentUrl(p.receiptPath);
                                        window.open(url, "_blank");
                                      } catch (err) {
                                        console.error("No se pudo abrir el ticket:", err);
                                        alert(lang==="en" ? "Couldn't open the receipt" : "No se pudo abrir el ticket");
                                      }
                                    }}>
                                <Paperclip size={12}/> {lang==="en" ? "Receipt" : "Ticket"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                        <div style={{textAlign:"right"}}>
                          <span style={{fontWeight:700,fontSize:15,color:p.type==="income"?"var(--green)":"var(--red)"}}>
                            {fmt(restante)}
                          </span>
                          {tienePagosParciales && (
                            <div style={{fontSize:11,color:"var(--gray-400)"}}>
                              {lang==="en"?"of":"de"} {fmt(p.amount)}
                            </div>
                          )}
                        </div>
                        {!isPaying && (
                          <span style={{fontSize:16,color:"var(--gray-400)",transition:"transform .2s",
                                        transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
                        )}
                      </div>
                    </div>

                    {tienePagosParciales && (
                      <div style={{padding:"0 14px 10px"}}>
                        <div className="limit-bar" style={{height:6}}>
                          <div className="limit-fill" style={{width:`${Math.min(100,Math.round((pagadoAmount/p.amount)*100))}%`}}/>
                        </div>
                      </div>
                    )}

                    {/* Formulario de abono parcial */}
                    {isPaying && (
                      <div style={{borderTop:"1px solid var(--border)",padding:"14px",background:"var(--gray-50)"}}
                           onClick={ev=>ev.stopPropagation()}>
                        <p style={{fontSize:13,fontWeight:600,marginBottom:10,color:"var(--green)"}}>
                          <CircleCheck size={14} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Register payment":"Registrar pago"}
                        </p>
                        <p style={{fontSize:12,color:"var(--gray-500)",marginBottom:10}}>
                          {lang==="en"?"Remaining":"Restante"}: <strong>{fmt(restante)}</strong>
                          {tienePagosParciales && <> · {lang==="en"?"Total":"Total"}: {fmt(p.amount)}</>}
                        </p>
                        {payError && <div className="alert alert-error" style={{marginBottom:10,padding:"8px 10px",fontSize:12}}>{payError}</div>}
                        <div style={{marginBottom:12}}>
                          <label style={{fontSize:12,color:"var(--gray-600)",fontWeight:500}}>
                            {lang==="en"?"Amount to register":"Importe a registrar"}
                          </label>
                          <input className="form-input" style={{marginTop:4}} type="number" step="0.01" min="0.01"
                                 max={restante} value={payAmount}
                                 onChange={ev=>setPayAmount(ev.target.value)} autoFocus/>
                        </div>
                        <div style={{marginBottom:12}}>
                          <label style={{fontSize:12,color:"var(--gray-600)",fontWeight:500}}>
                            {lang==="en"?"Detail":"Detalle"} <span style={{color:"var(--gray-400)",fontWeight:400}}>({lang==="en"?"optional":"opcional"})</span>
                          </label>
                          <input className="form-input" style={{marginTop:4}} type="text" value={payNota}
                                 onChange={ev=>setPayNota(ev.target.value)}
                                 placeholder={lang==="en"?"e.g. Julia, half of dinner...":"Ej: Julia, mitad de la cena..."}/>
                        </div>
                        <div style={{marginBottom:12}}>
                          <label style={{fontSize:12,color:"var(--gray-600)",fontWeight:500}}>
                            {lang==="en"?"Payment date":"Fecha del pago"}
                          </label>
                          {(() => {
                            const todayISO = toISODate(new Date());
                            const createdObj = getCreatedDateObj(p.createdAt);
                            const createdISO = createdObj ? toISODate(createdObj) : null;
                            const effectiveDate = payDate || todayISO;
                            return (
                              <>
                                <div style={{display:"flex",gap:8,marginTop:4,marginBottom:8,flexWrap:"wrap"}}>
                                  <button type="button" className="btn btn-outline btn-sm"
                                    style={effectiveDate===todayISO
                                      ? {borderColor:"var(--blue)",color:"var(--blue)",background:"var(--blue-l)"}
                                      : {}}
                                    onClick={()=>setPayDate(todayISO)}>
                                    <Calendar size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Today":"Hoy"}
                                  </button>
                                  {createdISO && (
                                    <button type="button" className="btn btn-outline btn-sm"
                                      style={effectiveDate===createdISO
                                        ? {borderColor:"var(--blue)",color:"var(--blue)",background:"var(--blue-l)"}
                                        : {}}
                                      onClick={()=>setPayDate(createdISO)}>
                                      <CalendarClock size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Creation date":"Fecha de creación"} ({fmtCreatedAt(p.createdAt, lang)})
                                    </button>
                                  )}
                                </div>
                                <input className="form-input" type="date" value={effectiveDate}
                                       onChange={ev=>setPayDate(ev.target.value)}/>
                              </>
                            );
                          })()}
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button className="btn btn-primary btn-sm" onClick={()=>confirmPay(p)} disabled={paySaving}>
                            {paySaving ? "..." : <><CircleCheck size={14} style={{display:"inline",verticalAlign:"-3px",marginRight:4}}/> {lang==="en"?"Confirm":"Confirmar"}</>}
                          </button>
                          <button className="btn btn-outline btn-sm" onClick={()=>{setPayingId(null);setPayError("");setPayNota("");}}>
                            {lang==="en"?"Cancel":"Cancelar"}
                          </button>
                        </div>
                      </div>
                    )}


                    {/* Detalle expandible */}
                    {isOpen && !isPaying && (
                      <div style={{borderTop:"1px solid var(--border)",padding:"12px 14px",background:"var(--gray-50)",
                                   display:"flex",flexDirection:"column",gap:6}}
                           onClick={e=>e.stopPropagation()}>
                        {createdLabel && <div style={{fontSize:13,color:"var(--gray-600)",display:"flex",alignItems:"center",gap:5}}><strong style={{display:"flex",alignItems:"center",gap:4}}><Calendar size={13}/> {lang==="en"?"Created":"Creado"}:</strong> {createdLabel}</div>}
                        {p.cat && <div style={{fontSize:13,color:"var(--gray-600)"}}><strong>{T("hist_cat")}:</strong> {p.cat}</div>}
                        {p.payment && <div style={{fontSize:13,color:"var(--gray-600)"}}><strong>{T("hist_account")}:</strong> {p.payment}</div>}
                        {p.due && <div style={{fontSize:13,color:isVencido?"var(--red)":"var(--gray-600)"}}><strong>{lang==="en"?"Due date":"Vencimiento"}:</strong> {new Date(p.due).toLocaleDateString("es-ES")}</div>}
                        {p.clienteId && clientesById[p.clienteId] && (
                          <div style={{fontSize:13,color:"var(--gray-600)",display:"flex",alignItems:"center",gap:5}}>
                            <strong style={{display:"flex",alignItems:"center",gap:4}}><Contact size={13}/> {lang==="en"?"Contact":"Contacto"}:</strong> {clientesById[p.clienteId].nombre}
                            {clientesById[p.clienteId].telefono && ` · ${clientesById[p.clienteId].telefono}`}
                          </div>
                        )}
                        {activeCustomFields.filter(f => p[f.key]).map(f => (
                          <div key={f.key} style={{fontSize:13,color:"var(--gray-600)"}}>
                            <strong>{f.name}:</strong> {p[f.key]}
                          </div>
                        ))}

                        {p.isCombined && (
                          <div style={{marginTop:4,padding:"8px 10px",
                                       background:"white",borderRadius:"var(--radius-sm)",border:"1px solid var(--border)"}}>
                            <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                              <div style={{fontSize:13,color:"var(--green)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
                                <CircleCheck size={13}/> {lang==="en"?"Income":"Ingreso"}: {fmt(p.incomeAmount||0)}
                              </div>
                              <div style={{fontSize:13,color:"var(--red)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
                                <TrendingDown size={13}/> {lang==="en"?"Expense":"Gasto"}: {fmt(p.expenseAmount||0)}
                              </div>
                              <div style={{fontSize:13,fontWeight:700,color:p.type==="income"?"var(--green)":"var(--red)"}}>
                                = {lang==="en"?"Net":"Neto"}: {p.type==="income"?"+":"-"}{fmt(p.amount)}
                              </div>
                            </div>
                            {p.expenseDesc && (
                              <div style={{fontSize:12,color:"var(--gray-500)",marginTop:6,display:"flex",alignItems:"center",gap:4}}>
                                <TrendingDown size={11}/> {lang==="en"?"Expense description":"Descripción del gasto"}: {p.expenseDesc}
                              </div>
                            )}
                          </div>
                        )}

                        {tienePagosParciales && (
                          <div style={{marginTop:4,marginBottom:4}}>
                            <div style={{fontSize:12,fontWeight:600,color:"var(--gray-600)",marginBottom:4}}>
                              <Banknote size={14} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Payment history":"Historial de abonos"}
                            </div>
                            <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              {(p.pagos||[]).map((pago,i)=>(
                                <div key={i} style={{display:"flex",flexDirection:"column",fontSize:12,
                                                      background:"var(--card)",borderRadius:6,padding:"6px 10px",
                                                      border:"1px solid var(--border)"}}>
                                  <div style={{display:"flex",justifyContent:"space-between"}}>
                                    <span style={{color:"var(--gray-500)"}}>{pago.date}</span>
                                    <span style={{fontWeight:600,color:"var(--green)"}}>{fmt(pago.amount)}</span>
                                  </div>
                                  {pago.nota && (
                                    <span style={{color:"var(--gray-500)",fontStyle:"italic",marginTop:2}}>
                                      {pago.nota}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div style={{fontSize:12,color:"var(--gray-600)",marginTop:6}}>
                              {lang==="en"?"Paid":"Pagado"}: <strong>{fmt(pagadoAmount)}</strong> · {lang==="en"?"Remaining":"Restante"}: <strong style={{color:"var(--red)"}}>{fmt(restante)}</strong>
                            </div>
                          </div>
                        )}

                        <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
                          <button className="btn btn-primary btn-sm"
                            onClick={ev=>startPay(p, ev)}>
                            <CircleCheck size={14} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Register payment":"Registrar pago"}
                          </button>
                          <button className="btn btn-outline btn-sm"
                            onClick={ev=>startEdit(p, ev)}>
                            <Pencil size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Edit":"Editar"}
                          </button>
                          <button className="btn btn-danger btn-sm" style={{display:"flex",alignItems:"center"}}
                            onClick={()=>{
                              if(window.confirm(lang==="en"?"Delete?":"¿Eliminar?")) {
                                if (p.receiptPath) {
                                  import("./receiptOcr").then(({ deleteAttachment }) => deleteAttachment(p.receiptPath));
                                }
                                deletePendiente(p.id);
                              }
                            }}>
                            <Trash2 size={14}/>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editingPendiente && (
        <EditPendienteModal pendiente={editingPendiente} onClose={()=>setEditingPendiente(null)} />
      )}
    </div>
  );
}
